/**
 * The notification bell.
 *
 * A count if there is anything unread, a panel behind it, and a link straight
 * to the document. It polls every 60 seconds — not a websocket, because a
 * dropped socket fails silently and a poll that stops just shows a stale
 * number until the next one lands.
 *
 * Each entry is a frozen copy of what happened, so re-reading an old one shows
 * what you were told at the time rather than what is true now.
 *
 * ---------------------------------------------------------------------------
 * ONE BELL PER SIDE — board, 17-09-2026
 *
 * The bell is drawn in both shells, and until today it drew the same list in
 * both: a buyer working through work orders in Service Procurement had to read
 * past goods receipts and purchase orders to find them. The board's instruction
 * is that the two modules' notifications are not to be mixed at all.
 *
 * So this component takes the side it is standing on and shows that side's
 * notifications and nothing else. What it does NOT do is decide which side a
 * notification is on: fn_notification_module does that, in the database, and
 * v_my_notifications hands the answer over in its `module` column (migration
 * 0137). The rule lives in one place because three things depend on it — this
 * list, the badge, and "mark all read" — and a rule copied into three places is
 * a rule that will disagree with itself.
 *
 * THE BADGE IS NOT COUNTED FROM THE LIST. It used to be, and that was only ever
 * right because the list was everything; filtered, it would have been the count
 * of "unread on this side, among the last forty of all kinds". The true count
 * per side comes from v_my_unread_counts, which counts every unread row.
 *
 * A notification that belongs to NEITHER side — one with no document behind it,
 * or a document type nobody has classified yet — is shown on both. Better seen
 * twice than never; see the header of 0137.
 * ---------------------------------------------------------------------------
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, Bell, CheckCheck, Clock, Info, TriangleAlert } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { dateTime } from '@/lib/format'
import {
  mayUseModule, moduleFor, moduleOfPath, rememberModule, type ModuleKey,
} from '@/lib/modules'
import { Spinner } from '@/components/ui'

/**
 * Where each kind of document lives.
 *
 * WO and WO_VARIATION were added 14-09-2026. Everything else had a route from
 * the day the bell was built; work orders did not, so a Project Manager tapping
 * "WO-TR5-20260914-001 needs your approval" got the panel closing and nothing
 * else - he then had to find the order himself, which for a notification whose
 * entire purpose is "come and act on this" is the one thing it must not do.
 *
 * Both Service entries still point INTO the other half of the application, and
 * that is deliberate even now the list is split: a notification that belongs to
 * neither side is shown on both, so a click from the Materials side may still
 * have to cross over. openNote() below handles that; see the note there.
 */
const ROUTE: Record<string, string> = {
  PR: '/prs', PO: '/pos', GRN: '/grns', MIN: '/mins', MRN: '/mrns',
  INDENT: '/indents', IST: '/ists', INVOICE: '/invoices', PAYABLE: '/payables',
  PERIOD: '/periods',
  WO: '/service/work-orders', WO_VARIATION: '/service/variations',
  MB: '/service/measurements', RA: '/service/ra-bills',
  // RETR added 20-09-2026. Reported from the Project Head's own screen: the
  // panel said "Retention release RETR-20260920-001 needs your approval,
  // Stage 1 of 2 — Project Head", he pressed it, the panel shut and nothing
  // else happened. A notification whose whole purpose is "come and act on
  // this" had no way to get him there.
  //
  // The document type was added to the database in 0154 and to this map
  // nowhere, and openNote() below returns quietly when the map has no entry.
  // scripts/check-notification-routes.py now makes that impossible to repeat:
  // any document type with an approval chain must appear here.
  RETR: '/service/retention-releases',
  ADV: '/service/advances',
  CRN: '/service/credit-notes',
  // 0214: a hire log sheet to certify, certified or returned.
  HIRE: '/service/hire-logs',
  // Vendor opening liabilities, 23-09-2026 (migration 0179). A payment has its
  // own page; a batch is approved on the list, so it is LIST_ONLY below.
  OPENING_PAYMENT: '/opening-liabilities/payments',
  OPENING_BATCH: '/opening-liabilities',
  // Project Finance, 02-10-2026 (migration 0193): the payment requisition.
  PRQ: '/finance/requisitions',
  // 0197: a guarantee or EMD nearing expiry - opens the register.
  BG: '/finance/guarantees',
  EMD: '/finance/guarantees',
  // 0199: a cash-credit limit due for renewal.
  CC: '/finance/funding',
  // 0234: a site cash flow to review or approve, approved or returned.
  SCF: '/finance/site-cashflow',
  // 0207: petty-cash bills to check, or checked.
  PCS: '/finance/petty-cash',
  // 0208: a month reopened by the CFO.
  PERIOD_PF: '/finance/period-close',
}

/**
 * Document types whose screen is a LIST, not a record.
 *
 * An accounting period has no page of its own. Appending an id to it would land
 * on a route that does not exist, and the router would bounce it - silently,
 * which is worse than not linking at all.
 *
 * WO_VARIATION left this set on 16-09-2026. It was in it because a variation
 * had no screen of its own - the register was the only place it appeared. It
 * has one now, /service/variations/:id, so "VO-... needs your approval" lands
 * on the variation itself with the Approve button on it, which is the entire
 * point of the notification.
 */
const LIST_ONLY = new Set(['PERIOD', 'OPENING_BATCH', 'BG', 'EMD', 'CC', 'PCS', 'PERIOD_PF', 'SCF'])

const TONE: Record<string, { icon: typeof Bell; cls: string }> = {
  CRITICAL: { icon: AlertTriangle, cls: 'text-red-600' },
  WARNING:  { icon: TriangleAlert, cls: 'text-amber-600' },
  ACTION:   { icon: Clock,         cls: 'text-blue-600' },
  INFO:     { icon: Info,          cls: 'text-slate-400' },
}

interface Note {
  id: number
  kind: string
  severity: string
  title: string
  body: string | null
  doc_type: string | null
  doc_id: string | null
  actor_name: string | null
  created_at: string
  is_unread: boolean
  /** 'materials', 'service', or null for one that belongs to neither. */
  module: string | null
}

export function NotificationBell({ module }: { module: ModuleKey }) {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [rows, setRows] = useState<Note[]>([])
  const [unread, setUnread] = useState(0)
  const [loading, setLoading] = useState(false)
  const boxRef = useRef<HTMLDivElement>(null)

  const load = useCallback(async () => {
    if (!profile) return
    setLoading(true)
    // THE LIST IS FILTERED IN THE QUERY, not after it arrives. Forty of this
    // side's notifications is forty of this side's — filtering afterwards would
    // have left a buyer with eight, because thirty-two of the last forty
    // happened on the other side of the house.
    const [list, counts] = await Promise.all([
      supabase.from('v_my_notifications').select('*')
        .or(`module.eq.${module},module.is.null`).limit(40),
      supabase.from('v_my_unread_counts').select('*').maybeSingle(),
    ])
    setRows((list.data ?? []) as Note[])
    const c: any = counts.data ?? {}
    setUnread(Number(module === 'service' ? c.service_unread
      : module === 'finance' ? c.finance_unread : c.materials_unread) || 0)
    setLoading(false)
  }, [profile, module])

  useEffect(() => {
    void load()
    const t = setInterval(() => { void load() }, 60_000)
    return () => clearInterval(t)
  }, [load])

  // Click anywhere else and the panel closes. Without this it stays open
  // behind whatever you clicked next, which feels broken.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  async function openNote(n: Note) {
    if (n.is_unread) {
      await supabase.rpc('rpc_mark_notification_read', { p_id: n.id })
      setRows((prev) => prev.map((r) => (r.id === n.id ? { ...r, is_unread: false } : r)))
      setUnread((u) => Math.max(0, u - 1))
    }
    setOpen(false)
    if (!n.doc_type || !n.doc_id) return

    const base = ROUTE[n.doc_type]
    if (!base) {
      // Should be unreachable -- check-notification-routes.py fails the build
      // for any chain document type missing from ROUTE. If it ever happens,
      // say so rather than swallowing the click, because a dead button that
      // looks alive is the fault that took a fortnight to be reported.
      console.error(
        `[notifications] no route for document type "${n.doc_type}" — ` +
        `${n.doc_id} cannot be opened from the bell. Add it to ROUTE in ` +
        `NotificationBell.tsx.`)
      return
    }
    const to = LIST_ONLY.has(n.doc_type) ? base : `${base}/${n.doc_id}`

    // CROSSING BETWEEN THE TWO SIDES.
    //
    // The two halves of the application are one router, so navigate() reaches
    // either. Since 17-09-2026 the list only ever holds this side's
    // notifications and those belonging to neither, so a crossing is now rare
    // rather than routine - but the two guards still earn their place:
    //
    //   1. A person who may not open that side must not be sent there. It
    //      cannot happen today - nothing notifies a store-man about a work
    //      order - but the guard costs a line, and without it a future
    //      notification would dump him on a shell that immediately bounces him
    //      back, which reads as a broken link rather than as a boundary.
    //   2. The remembered side has to follow him. Otherwise he acts on the work
    //      order, signs out, signs back in tomorrow and lands on Materials
    //      wondering where it went.
    const side = moduleOfPath(to)
    if (!mayUseModule(profile?.role, side)) return
    rememberModule(side)
    navigate(to)
  }

  /**
   * Mark all read — ON THIS SIDE ONLY.
   *
   * The RPC takes the module and clears nothing else. Before 0137 it cleared
   * every unread row the person had, which after the split would have meant a
   * buyer tidying up his Service notifications silently emptying his Materials
   * badge - the same intermixing by another route, and the one that loses work
   * rather than merely showing it in the wrong place.
   */
  async function markAll() {
    await supabase.rpc('rpc_mark_all_notifications_read', { p_module: module })
    setRows((prev) => prev.map((r) => ({ ...r, is_unread: false })))
    setUnread(0)
  }

  const here = moduleFor(module).title

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={unread ? `${unread} unread ${here} notifications` : `${here} notifications`}
        title={`${here} notifications`}
        className="relative flex items-center rounded-lg px-2 py-1.5 text-slate-600 hover:bg-slate-100"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-[22rem] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg sm:w-[26rem]">
          <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2">
            <span className="min-w-0 text-sm font-semibold text-slate-800">
              Notifications{unread > 0 && <span className="ml-1.5 pill-amber">{unread} new</span>}
              {/* Which side these are. Two bells now show two different lists,
                  and a person who does not know that would think half of them
                  had gone missing. */}
              <span className="block truncate text-[11px] font-normal text-slate-500">{here}</span>
            </span>
            {unread > 0 && (
              <button type="button" onClick={markAll}
                      title={`Mark every ${here} notification read. The other side is not touched.`}
                      className="flex shrink-0 items-center gap-1 text-xs font-medium text-oval-700 hover:underline">
                <CheckCheck className="h-3.5 w-3.5" /> Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[26rem] overflow-y-auto">
            {loading && rows.length === 0 && (
              <div className="flex justify-center py-6"><Spinner /></div>
            )}
            {!loading && rows.length === 0 && (
              <p className="px-3 py-8 text-center text-sm text-slate-500">
                Nothing yet. You will be told here when a {here.toLowerCase()} document
                needs you.
              </p>
            )}
            {rows.map((n) => {
              const t = TONE[n.severity] ?? TONE.INFO
              const Icon = t.icon
              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => openNote(n)}
                  className={`flex w-full gap-2.5 border-b border-slate-100 px-3 py-2.5 text-left last:border-0 hover:bg-slate-50 ${
                    n.is_unread ? 'bg-oval-50/60' : ''}`}
                >
                  <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${t.cls}`} />
                  <span className="min-w-0 flex-1">
                    <span className={`block text-xs ${n.is_unread ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
                      {n.title}
                    </span>
                    {n.body && (
                      <span className="mt-0.5 block text-[11px] leading-snug text-slate-500">{n.body}</span>
                    )}
                    <span className="mt-1 block text-[10px] text-slate-400">
                      {dateTime(n.created_at)}
                      {n.actor_name ? ` · ${n.actor_name}` : ''}
                    </span>
                  </span>
                  {n.is_unread && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-oval-600" />}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
